import { Component, OnInit, Inject, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { SecurityPrincipalDto } from 'src/app/data-transfer/entities/security-principal-dto';
import { CreateDataExchangeRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/create-data-exchange-request-dto';
import { CreateDataExchangeSubRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/create-data-exchange-sub-request-dto';
import { DataExchangeSubRequestTypeDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-type-dto';
import { DialogActions } from 'src/app/model/dictionary';

export interface CreateDataExchangeRequestDialogData {
  allPrincipals: SecurityPrincipalDto[];
  packagingSystemIds?: number[];
  packagingUnitIds?: number[];
  componentIds?: number[];
  materialIds?: number[];
}

@Component({
  selector: 'app-create-data-exchange-request-dialog',
  templateUrl: './create-data-exchange-request-dialog.component.html',
  styleUrls: ['./create-data-exchange-request-dialog.component.scss']
})
export class CreateDataExchangeRequestDialogComponent implements OnInit {

  @ViewChild('principalInput') principalInput!: ElementRef<HTMLInputElement>;

  separatorKeysCodes: number[] = [ENTER, COMMA];
  requestForm!: FormGroup;

  filteredPrincipals!: Observable<SecurityPrincipalDto[]>;
  allPrincipals: SecurityPrincipalDto[] = [];
  selectedPrincipal: SecurityPrincipalDto | null = null;

  packagingSystemIds: number[] = [];
  packagingUnitIds: number[] = [];
  componentIds: number[] = [];
  materialIds: number[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: CreateDataExchangeRequestDialogData,
    private dialogRef: MatDialogRef<CreateDataExchangeRequestDialogComponent>,
    private formBuilder: FormBuilder
  ) {
    if (this.data) {
      this.allPrincipals = this.data.allPrincipals ?? [];
      this.packagingSystemIds = this.data.packagingSystemIds ?? [];
      this.packagingUnitIds = this.data.packagingUnitIds ?? [];
      this.componentIds = this.data.componentIds ?? [];
      this.materialIds = this.data.materialIds ?? [];
    }
  }

  get principalCtrl(): FormControl {
    return this.requestForm?.controls['principalControl'] as FormControl;
  }

  get nameCtrl(): FormControl {
    return this.requestForm?.controls['nameControl'] as FormControl;
  }

  get commentCtrl(): FormControl {
    return this.requestForm?.controls['commentControl'] as FormControl;
  }

  get totalItemCount(): number {
    return this.packagingSystemIds.length + this.packagingUnitIds.length +
      this.componentIds.length + this.materialIds.length;
  }

  ngOnInit(): void {
    this.requestForm = this.formBuilder.group({
      nameControl: ['', Validators.required],
      principalControl: [null],
      commentControl: ['']
    });

    this.filteredPrincipals = this.principalCtrl.valueChanges.pipe(
      startWith(''),
      map(value => (typeof value === 'string' ? value : (value?.name ?? ''))),
      map(name => name ? this._filter(name) : this.allPrincipals.slice())
    );
  }

  selectPrincipal(principal: SecurityPrincipalDto): void {
    this.selectedPrincipal = principal;
    this.principalInput.nativeElement.value = '';
    this.principalCtrl.setValue('');
    this.principalCtrl.disable();
  }

  removePrincipal(): void {
    this.selectedPrincipal = null;
    this.principalInput.nativeElement.value = '';
    this.principalCtrl.setValue('');
    this.principalCtrl.enable();
  }

  confirm(): void {
    this.requestForm.markAllAsTouched();
    if (this.requestForm.invalid || !this.selectedPrincipal) { return; }

    const subRequests: CreateDataExchangeSubRequestDto[] = [
      ...this.packagingSystemIds.map(id => ({ type: DataExchangeSubRequestTypeDto.PackagingSystem, packagingId: id })),
      ...this.packagingUnitIds.map(id => ({ type: DataExchangeSubRequestTypeDto.PackagingUnit, packagingId: id })),
      ...this.componentIds.map(id => ({ type: DataExchangeSubRequestTypeDto.PackagingComponent, packagingId: id })),
      ...this.materialIds.map(id => ({ type: DataExchangeSubRequestTypeDto.MultiMaterial, packagingId: id }))
    ];

    const dto: CreateDataExchangeRequestDto = {
      name: this.nameCtrl.value.trim(),
      receivingSecurityPrincipalId: this.selectedPrincipal.id,
      subRequests,
      initialComment: this.commentCtrl.value?.trim() || undefined
    };

    this.dialogRef.close({ event: DialogActions.CONFIRM, dto });
  }

  cancel(): void {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }

  private _filter(value: string): SecurityPrincipalDto[] {
    const filterValue = value.toLowerCase();
    return this.allPrincipals
      .filter(p => p !== this.selectedPrincipal)
      .filter(p => p.name.toLowerCase().includes(filterValue));
  }
}
